import React, { forwardRef, useImperativeHandle } from 'react'
import { Form } from 'antd'
import { useForm, FormProvider } from 'react-hook-form'

import { Loader } from '@app/components'
import { Switch } from '@app/components/util'

import type { FieldValues } from 'react-hook-form'
import type { LntFormRef, LntFormProps } from './types'
import type { ReactElement, ForwardedRef } from 'react'
import type { SubmitHandler, SubmitErrorHandler } from 'react-hook-form/dist/types/form'

const { Case } = Switch

const Component = <FormData extends FieldValues>(
  props: LntFormProps<FormData>,
  ref: ForwardedRef<LntFormRef<FormData>>
): ReactElement<LntFormProps<FormData>> => {
  const {
    formInstance: outerFormInstance,
    formSettings,
    loading,
    children,
    onSubmit,
    onSubmitFailed,
    ...restProps
  } = props

  const innerFormInstance = useForm<FormData>(formSettings)
  const formInstance = outerFormInstance ?? innerFormInstance

  const { handleSubmit } = formInstance

  const onValidationSuccess: SubmitHandler<FormData> = value => onSubmit?.(value)

  const onValidationFailure: SubmitErrorHandler<FormData> = errors => onSubmitFailed?.(errors)

  const submitHandler = handleSubmit(onValidationSuccess, onValidationFailure)

  useImperativeHandle<LntFormRef<FormData>, LntFormRef<FormData>>(ref, () => ({
    formInstance
  }))

  return (
    // TODO: [LNT-1576] заменить на <Show fallback={ <Loader /> }>... после доработки компонента Show
    <Switch fallback={ <Loader /> }>
      <Case condition={ !loading }>
        <FormProvider { ...formInstance }>
          <Form
            { ...restProps }
            onFinish={ submitHandler }
          >
            { children as React.ReactNode }
          </Form>
        </FormProvider>
      </Case>
    </Switch>
  )
}

export const LntForm = forwardRef(Component) as <FormData extends FieldValues>(
  props: LntFormProps<FormData> & { ref?: ForwardedRef<LntFormRef<FormData>> }
) => ReactElement<LntFormProps<FormData>>
